import React, { useState, useEffect } from "react";
import {
  StyledButtonSalvar,
  StyledButtonVoltar,
} from "../default_button/styles";
import { privacyPolicyHTML } from "../terms/privacy_policy/privacy_policy";
import { termsOfUse } from "../terms/terms_of_use/terms_of_use.js";
import {
  MainContainer,
  ContentWrapper,
  HeaderSection,
  Title,
  FirstRow,
  Subtitle,
  Row,
  Label,
  RadioLabel,
  Input,
  LeftColumn,
  RightColumn,
  Divider,
  FormContainer,
  ButtonContainer,
  StyledSelect,
  ShowInput,
  BtnShowPassword,
  InputWrapper,
  RadioGroup,
  CheckboxGroup,
  HiddenCheckbox,
  CheckboxContainer,
  StyledCheckbox,
  LabelText,
  CheckboxRow,
  CheckboxLabel,
  FieldUserForm,
  FieldUserCard,
  FieldUserHint,
  FieldUserEmailNotice,
  FieldUserActions,
  FieldUserPage,
  FieldUserFields,
  FieldUserAreaBlock,
} from "./styles";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import { addUserToUsers } from "../../store/modules/app_data/actions";
import { useSelector, useDispatch } from "react-redux";
import { appStatus } from "../../store/modules/app_status/actions";
import { EyeIcon, EyeOffIcon, Loader } from "lucide-react";
import { useHookFormMask } from "use-mask-input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ErrorForm } from "./ErrorForm";
import {
  registerUserSchema,
  fieldUserCreateSchema,
  FIELD_AREA_ROLES,
} from "./registerUserValidation";
import { userService } from "../../services/userService.js";
import { projectService } from "../../services/projectService.js";
import { guardianAreaService } from "../../services/guardianAreaService.js";
import { handleCepChange } from "../../api/requests/cep.js";
import {
  canManageUsers,
  isEngineeringUser,
} from "../../utils/userRoles";
import PageHeader from "../page_header";

const AREA_ROLE_LABELS = {
  TEAM: "Membro",
  MANAGER: "Gestor",
};

/** GET /projects/ → { projects_project: [...] } ou array. */
function normalizeProjectsPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.projects_project)) return payload.projects_project;
  if (Array.isArray(payload?.data?.projects_project)) {
    return payload.data.projects_project;
  }
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.projects)) return payload.projects;
  return [];
}

/** GET /guardian/list_areas/ → array (ou wrappers conhecidos). */
function normalizeGuardianAreasPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.guardian_areas)) return payload.guardian_areas;
  if (Array.isArray(payload?.data?.guardian_areas)) {
    return payload.data.guardian_areas;
  }
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function getAreaProjectId(area) {
  if (!area || typeof area !== "object") return null;
  if (area.project_detail?.id != null) return area.project_detail.id;
  if (area.project_id != null) return area.project_id;
  if (typeof area.project === "object" && area.project?.id != null) {
    return area.project.id;
  }
  if (area.project != null && typeof area.project !== "object") {
    return area.project;
  }
  return null;
}

const fieldNames = {
  password: "Senha",
  email: "E-mail",
  cpf: "CPF",
  cnpj: "CNPJ",
  rg: "RG",
  phone: "Telefone",
  full_name: "Nome completo",
  cep: "CEP",
  user_type: "Tipo de acesso",
  non_field_errors: "Erro",
};

const errorTranslations = {
  "This password is too short. It must contain at least 8 characters.":
    "A senha é muito curta. Deve conter pelo menos 8 caracteres.",
  "This password is too common.": "Esta senha é muito comum.",
  "This password is entirely numeric.":
    "A senha não pode ser composta apenas por números.",
  "This field may not be blank.": "Este campo não pode estar em branco.",
  "This field is required.": "Este campo é obrigatório.",
  "Enter a valid email address.": "Informe um endereço de e-mail válido.",
  "A user with that email already exists.":
    "Já existe um usuário com este e-mail.",
  "Já existe um usuário cadastrado com este email.":
    "Já existe um usuário cadastrado com este e-mail.",
};

const translateError = (msg) => errorTranslations[msg] || msg;

function formatApiErrors(errorData) {
  if (!errorData || typeof errorData !== "object") {
    return "Algo deu errado ao tentar processar sua requisição.";
  }
  const messages = Object.entries(errorData)
    .map(([field, errors]) => {
      const label = fieldNames[field] || field;
      const fieldErrors = Array.isArray(errors)
        ? errors.map(translateError).join("<br/>• ")
        : translateError(errors);
      return `<b>${label}:</b><br/>• ${fieldErrors}`;
    })
    .join("<br/><br/>");
  return messages || "Algo deu errado ao tentar processar sua requisição.";
}

/** Formulário ENG: nome, e-mail, tipo PROP/GUARDIAN e vínculo Membro/Gestor em áreas. */
function FieldUserRegisterForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
    reset,
  } = useForm({
    resolver: zodResolver(fieldUserCreateSchema),
    defaultValues: {
      full_name: "",
      email: "",
      user_type: "GUARDIAN",
    },
  });

  const [loadingOptions, setLoadingOptions] = useState(true);
  const [projects, setProjects] = useState([]);
  const [guardianAreas, setGuardianAreas] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [selectedRole, setSelectedRole] = useState(FIELD_AREA_ROLES.TEAM);
  const [areas, setAreas] = useState([]); // [{ id_code, name, role }]

  useEffect(() => {
    const load = async () => {
      setLoadingOptions(true);
      try {
        const [projectsRes, areasRes] = await Promise.all([
          projectService.getProjects(),
          guardianAreaService.getGuardianAreas(),
        ]);
        setProjects(normalizeProjectsPayload(projectsRes));
        setGuardianAreas(normalizeGuardianAreasPayload(areasRes));
      } catch (err) {
        console.error("Erro ao carregar projetos/áreas:", err);
        setProjects([]);
        setGuardianAreas([]);
      } finally {
        setLoadingOptions(false);
      }
    };
    load();
  }, []);

  const filteredAreas = projectId
    ? guardianAreas.filter(
        (area) => String(getAreaProjectId(area)) === String(projectId)
      )
    : [];

  const handleAddArea = () => {
    if (!selectedArea) return;
    const area = filteredAreas.find(
      (a) => String(a.id_code || a.id) === String(selectedArea)
    );
    if (!area) return;
    const id_code = String(area.id_code || area.id);
    if (areas.some((a) => a.id_code === id_code)) return;
    const role =
      selectedRole === FIELD_AREA_ROLES.MANAGER
        ? FIELD_AREA_ROLES.MANAGER
        : FIELD_AREA_ROLES.TEAM;
    setAreas((prev) => [
      ...prev,
      {
        id_code,
        name: area.name || id_code,
        role,
      },
    ]);
    setSelectedArea("");
  };

  const handleRemoveArea = (id_code) => {
    setAreas((prev) => prev.filter((a) => a.id_code !== id_code));
  };

  const handleComeBack = () => {
    dispatch(appStatus("Usuários"));
    navigate("/welcome");
  };

  const onSubmit = async (data) => {
    try {
      const res = await userService.createFieldUser({
        full_name: data.full_name,
        email: data.email,
        user_type: data.user_type,
        areas: areas.map(({ id_code, role }) => ({ id_code, role })),
      });

      const assigned = res?.areas_assigned || [];
      await Swal.fire({
        title: "Sucesso!",
        html: `
          <p style="text-align:left;margin:0 0 0.75rem">
            Usuário cadastrado${
              assigned.length
                ? ` e vinculado a ${assigned.length} área(s)`
                : ""
            }.
          </p>
          <p style="text-align:left;margin:0">
            Ele receberá um <strong>e-mail de boas-vindas</strong> com:
          </p>
          <ul style="text-align:left;margin:0.5rem 0 0;padding-left:1.25rem">
            <li>link para baixar o aplicativo Guardian</li>
            <li>manual de uso</li>
            <li>acesso por e-mail e senha temporária</li>
          </ul>
        `,
        icon: "success",
        confirmButtonText: "OK",
      });

      try {
        reset();
        setAreas([]);
        setProjectId("");
        dispatch(addUserToUsers(res));
      } catch {
        /* ignore */
      }
      dispatch(appStatus("Usuários"));
      navigate("/welcome");
    } catch (error) {
      console.error("Erro no cadastro simplificado:", error);
      Swal.fire({
        title: "Erro ao cadastrar!",
        html: `<div style="text-align: left; font-size: 14px; line-height: 1.8">${formatApiErrors(
          error.response?.data
        )}</div>`,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "var(--danger)",
      });
    }
  };

  return (
    <FieldUserPage>
      <PageHeader
        title="Cadastrar usuário"
        actionLabel="Voltar"
        onAction={handleComeBack}
      />

      <FieldUserHint>
        Cadastre um usuário Guardian ou Proprietário para receber alertas.
        Informe nome, e-mail, tipo de acesso e a posição (
        <strong>Membro</strong> ou <strong>Gestor</strong>). Opcionalmente
        vincule a uma ou mais áreas com essa posição.
      </FieldUserHint>

      <FieldUserCard>
        <FieldUserEmailNotice>
          <strong>Ao confirmar o cadastro</strong>
          <ul>
            <li>
              o usuário recebe um e-mail de boas-vindas com o link para baixar o
              aplicativo Guardian
            </li>
            <li>o manual de uso em PDF</li>
            <li>e-mail e senha temporária para acessar a plataforma</li>
          </ul>
        </FieldUserEmailNotice>

        <FieldUserForm onSubmit={handleSubmit(onSubmit)}>
          <FieldUserFields>
            <Row>
              <Label htmlFor="full_name">Nome completo</Label>
              <ShowInput
                type="text"
                id="full_name"
                placeholder="Nome completo"
                {...register("full_name")}
              />
              <ErrorForm message={errors.full_name?.message} />
            </Row>
            <Row>
              <Label htmlFor="email">E-mail</Label>
              <ShowInput
                type="email"
                id="email"
                placeholder="email@exemplo.com"
                {...register("email")}
              />
              <ErrorForm message={errors.email?.message} />
            </Row>
            <Row>
              <Label htmlFor="user_type">Tipo de acesso</Label>
              <StyledSelect id="user_type" {...register("user_type")}>
                <option value="GUARDIAN">Guardian</option>
                <option value="PROP">Proprietário</option>
              </StyledSelect>
              <ErrorForm message={errors.user_type?.message} />
            </Row>
            <Row>
              <Label htmlFor="field_position">Posição</Label>
              <StyledSelect
                id="field_position"
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value={FIELD_AREA_ROLES.TEAM}>Membro</option>
                <option value={FIELD_AREA_ROLES.MANAGER}>Gestor</option>
              </StyledSelect>
            </Row>
            <Row>
              <Label htmlFor="field_project">Projeto (opcional)</Label>
              <StyledSelect
                id="field_project"
                value={projectId}
                disabled={loadingOptions}
                onChange={(e) => {
                  setProjectId(e.target.value);
                  setSelectedArea("");
                  // Mantém áreas já adicionadas de outros projetos
                }}
              >
                <option value="">
                  {loadingOptions
                    ? "Carregando projetos..."
                    : projects.length === 0
                      ? "Nenhum projeto disponível"
                      : "Selecione um projeto"}
                </option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title || project.name || `Projeto #${project.id}`}
                  </option>
                ))}
              </StyledSelect>
            </Row>

            <FieldUserAreaBlock>
              <Label htmlFor="field_area">Área (opcional)</Label>
              {!projectId ? (
                <span className="area-empty" style={{ color: "var(--text-muted, var(--text-secondary))" }}>
                  Selecione um projeto acima para listar as áreas disponíveis.
                </span>
              ) : (
                <div className="area-controls">
                  <StyledSelect
                    id="field_area"
                    value={selectedArea}
                    disabled={loadingOptions}
                    onChange={(e) => setSelectedArea(e.target.value)}
                  >
                    <option value="">
                      {loadingOptions
                        ? "Carregando áreas..."
                        : filteredAreas.length === 0
                          ? "Nenhuma área neste projeto"
                          : "Selecione uma área"}
                    </option>
                    {filteredAreas
                      .filter(
                        (area) =>
                          !areas.some(
                            (a) =>
                              a.id_code === String(area.id_code || area.id)
                          )
                      )
                      .map((area) => (
                        <option
                          key={area.id_code || area.id}
                          value={area.id_code || area.id}
                        >
                          {area.name}
                        </option>
                      ))}
                  </StyledSelect>
                  <StyledButtonSalvar
                    type="button"
                    onClick={handleAddArea}
                    disabled={!selectedArea}
                    style={{ minWidth: "auto", padding: "8px 16px" }}
                  >
                    Adicionar
                  </StyledButtonSalvar>
                </div>
              )}
              {areas.length > 0 && (
                <div className="area-tags">
                  {areas.map((entry) => (
                    <span key={entry.id_code}>
                      {entry.name} — {AREA_ROLE_LABELS[entry.role]}
                      <button
                        type="button"
                        onClick={() => handleRemoveArea(entry.id_code)}
                        aria-label="Remover área"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {projectId && !loadingOptions && filteredAreas.length === 0 && (
                <span className="area-empty">
                  Nenhuma área encontrada para este projeto.
                </span>
              )}
            </FieldUserAreaBlock>
          </FieldUserFields>

          <FieldUserActions>
            <StyledButtonVoltar type="button" onClick={handleComeBack}>
              Voltar
            </StyledButtonVoltar>
            <StyledButtonSalvar type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loader size={18} /> : "Cadastrar"}
            </StyledButtonSalvar>
          </FieldUserActions>
        </FieldUserForm>
      </FieldUserCard>
    </FieldUserPage>
  );
}

const FullInternRegisterUser = (props) => {
  const { userType = null } = props || {};
  const [isPasswordVisible, seIsPasswordVisible] = useState(false);
  const [personType, setPersonType] = useState("fisica");
  const [cep, setCep] = useState("");

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, errors },
    setValue,
    watch,
    reset,
  } = useForm({
    resolver: zodResolver(registerUserSchema),
    defaultValues: {
      personType: personType,
    },
  });

  const showTermsOfUse = () => {
    Swal.fire({
      title: "Termos de Uso",
      html: termsOfUse,
      width: "80%",
      confirmButtonColor: "var(--info)",
    });
  };

  const showPrivacyPolicy = () => {
    Swal.fire({
      title: "Política de Privacidade",
      html: privacyPolicyHTML,
      width: "80%",
      confirmButtonColor: "var(--info)",
    });
  };

  const handlePersonTypeChange = (type) => {
    setPersonType(type);
    setValue("personType", type);
  };

  const registerWithMask = useHookFormMask(register);

  async function handleCEPBlur(e) {
    const rawCEP = e.target.value.replace(/\D/g, "");
    try {
      const data = await handleCepChange(rawCEP);
      if (!data) return;
      setValue("street", data.logradouro || data.street || "");
      setValue("district", data.bairro || data.neighborhood || "");
      setValue("city", data.localidade || data.city || "");
      setValue("state", data.uf || data.state || "");
    } catch (error) {
      console.error("Erro ao buscar CEP:", error.message);
    }
  }

  async function onSubmit(data) {
    const formValues = { ...data };
    delete formValues.id;

    try {
      const res = await userService.registerUser(formValues);

      Swal.fire({
        title: "Sucesso!",
        text: "Sua requisição foi processada com sucesso.",
        icon: "success",
        confirmButtonText: "OK",
      });

      try {
        reset();
        dispatch(addUserToUsers(res));
        navigate("/welcome");
      } catch {
        dispatch(appStatus("Usuários"));
      }

      dispatch(appStatus("Usuários"));
      navigate("/welcome");
    } catch (error) {
      console.error("Erro no envio:", error);
      Swal.fire({
        title: "Erro ao cadastrar!",
        html: `<div style="text-align: left; font-size: 14px; line-height: 1.8">${formatApiErrors(
          error.response?.data
        )}</div>`,
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "var(--danger)",
      });
    }
  }

  const shouldShowUserTypeSelect = !userType;

  useEffect(() => {
    if (userType) {
      setValue("user_type", userType);
    }
  }, [userType, setValue]);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleComeBack = () => {
    dispatch(appStatus("Usuários"));
    navigate("/welcome");
  };

  return (
    <MainContainer>
      <ContentWrapper>
        <HeaderSection>
          <Title>Cadastrar novo usuário</Title>
        </HeaderSection>
        <StyledButtonVoltar
          onClick={handleComeBack}
          style={{ margin: "0px 65px 0px 0px" }}
        >
          Voltar
        </StyledButtonVoltar>
        <FormContainer onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Label>Selecione o tipo de pessoa:</Label>
          </Row>
          <ButtonContainer>
            <RadioGroup>
              <Row>
                <RadioLabel>
                  <Input
                    type="radio"
                    {...register("personType")}
                    value="fisica"
                    checked={personType === "fisica"}
                    onChange={() => handlePersonTypeChange("fisica")}
                  />
                  Pessoa Física
                </RadioLabel>
              </Row>
              <Row>
                <RadioLabel>
                  <Input
                    type="radio"
                    {...register("personType")}
                    value="juridica"
                    checked={personType === "juridica"}
                    onChange={() => handlePersonTypeChange("juridica")}
                  />
                  Pessoa Jurídica
                </RadioLabel>
              </Row>
            </RadioGroup>
          </ButtonContainer>

          <Divider />

          <FirstRow>
            <Subtitle>📌 Dados Pessoais</Subtitle>
          </FirstRow>
          <LeftColumn>
            <Row>
              <Label>Nome completo:</Label>
              <ShowInput
                type="text"
                id="full_name"
                {...register("full_name")}
              />
              <ErrorForm message={errors.full_name?.message} />
            </Row>

            {personType === "fisica" ? (
              <>
                <Row>
                  <Label>RG:</Label>
                  <ShowInput type="text" id="rg" {...register("rg")} />
                  <ErrorForm message={errors.rg?.message} />
                </Row>
                <Row>
                  <Label>CPF:</Label>
                  <ShowInput
                    type="text"
                    id="cpf"
                    {...registerWithMask("cpf", "999.999.999-99")}
                  />
                  <ErrorForm message={errors.cpf?.message} />
                </Row>
              </>
            ) : (
              <>
                <Row>
                  <Label>CNPJ:</Label>
                  <ShowInput
                    type="text"
                    id="cnpj"
                    {...registerWithMask("cnpj", "99.999.999/9999-99")}
                  />
                  <ErrorForm message={errors.cnpj?.message} />
                </Row>
              </>
            )}
          </LeftColumn>
          <RightColumn>
            <Row>
              <Label>Email:</Label>
              <ShowInput type="email" id="email" {...register("email")} />
              <ErrorForm message={errors.email?.message} />
            </Row>

            <Row>
              <Label>Senha:</Label>
              <InputWrapper>
                <ShowInput
                  type={isPasswordVisible ? "text" : "password"}
                  id="password"
                  {...register("password")}
                />
                <BtnShowPassword
                  type="button"
                  onClick={() => seIsPasswordVisible(!isPasswordVisible)}
                >
                  {isPasswordVisible ? (
                    <EyeIcon size={20} style={{ color: "var(--text-secondary)" }} />
                  ) : (
                    <EyeOffIcon
                      size={20}
                      style={{ color: "var(--text-secondary)" }}
                    />
                  )}
                </BtnShowPassword>
              </InputWrapper>
              <ErrorForm message={errors.password?.message} />
            </Row>

            <Row>
              <Label>Confirmar Senha:</Label>
              <InputWrapper>
                <ShowInput
                  type={isPasswordVisible ? "text" : "password"}
                  id="confirm-password"
                  {...register("password_confirmation")}
                />
                <BtnShowPassword
                  type="button"
                  onClick={() => seIsPasswordVisible(!isPasswordVisible)}
                >
                  {isPasswordVisible ? (
                    <EyeIcon size={20} style={{ color: "var(--text-secondary)" }} />
                  ) : (
                    <EyeOffIcon
                      size={20}
                      style={{ color: "var(--text-secondary)" }}
                    />
                  )}
                </BtnShowPassword>
              </InputWrapper>
              <ErrorForm message={errors.password_confirmation?.message} />
            </Row>

            <Row>
              <Label>WhatsApp:</Label>
              <ShowInput
                type="text"
                id="phone"
                {...registerWithMask("phone", "(99) 99999-9999")}
              />
              <ErrorForm message={errors.phone?.message} />
            </Row>

            {shouldShowUserTypeSelect && (
              <Row>
                <Label>Tipo de acesso:</Label>
                <StyledSelect id="user_type" {...register("user_type")}>
                  <option value="">Selecione…</option>
                  <option value="COM">COM</option>
                  <option value="ENG">ENG</option>
                  <option value="ADM">ADM</option>
                  <option value="Regular">Regular</option>
                  <option value="PROP">PROP</option>
                  <option value="GUARDIAN">GUARDIAN</option>
                </StyledSelect>
                <ErrorForm message={errors.user_type?.message} />
              </Row>
            )}
          </RightColumn>

          <Divider />
          <FirstRow>
            <Subtitle>📌 Endereço</Subtitle>
          </FirstRow>
          <LeftColumn>
            <Row>
              <Label>CEP:</Label>
              <ShowInput
                type="text"
                id="cep"
                {...registerWithMask("cep", "99999-999", {
                  onBlur: handleCEPBlur,
                })}
                onChange={(e) => setCep(e.target.value)}
              />
              <ErrorForm message={errors.cep?.message} />
            </Row>

            <Row>
              <Label>Rua:</Label>
              <ShowInput
                type="text"
                id="street"
                {...register("street")}
                disabled={!cep}
                placeholder={
                  cep ? "" : "Informe o CEP para autocompletar a rua"
                }
              />
              <ErrorForm message={errors.street?.message} />
            </Row>

            <Row>
              <Label>Bairro:</Label>
              <ShowInput
                type="text"
                id="district"
                {...register("district")}
                disabled={!cep}
                placeholder={
                  cep ? "" : "Informe o CEP para autocompletar o bairro"
                }
              />
              <ErrorForm message={errors.district?.message} />
            </Row>

            <Row>
              <Label>Cidade:</Label>
              <ShowInput
                type="text"
                id="city"
                {...register("city")}
                disabled={!cep}
                placeholder={
                  cep ? "" : "Informe o CEP para autocompletar a cidade"
                }
              />
              <ErrorForm message={errors.city?.message} />
            </Row>
          </LeftColumn>
          <RightColumn>
            <Row>
              <Label>UF:</Label>
              <ShowInput
                type="text"
                id="state"
                {...register("state")}
                disabled={!cep}
                placeholder={
                  cep ? "" : "Informe o CEP para autocompletar o estado"
                }
              />
              <ErrorForm message={errors.state?.message} />
            </Row>

            <Row>
              <Label>Número:</Label>
              <ShowInput type="text" id="number" {...register("number")} />
              <ErrorForm message={errors.number?.message} />
            </Row>
            <Row>
              <Label>Complemento:</Label>
              <ShowInput
                type="text"
                id="complement"
                {...register("complement")}
              />
              <ErrorForm message={errors.complement?.message} />
            </Row>
          </RightColumn>

          <CheckboxGroup>
            <CheckboxRow>
              <CheckboxLabel>
                <CheckboxContainer>
                  <HiddenCheckbox
                    type="checkbox"
                    id="accept_terms_of_use"
                    {...register("accept_terms_of_use")}
                  />
                  <StyledCheckbox checked={watch("accept_terms_of_use")} />
                </CheckboxContainer>
                <span>Aceito os </span>
                <LabelText
                  onClick={(e) => {
                    e.preventDefault();
                    showTermsOfUse();
                  }}
                >
                  termos de uso
                </LabelText>
              </CheckboxLabel>
              <ErrorForm message={errors.accept_terms_of_use?.message} />
            </CheckboxRow>

            <CheckboxRow>
              <CheckboxLabel>
                <CheckboxContainer>
                  <HiddenCheckbox
                    type="checkbox"
                    id="accept_privacy_politics"
                    {...register("accept_privacy_politics")}
                  />
                  <StyledCheckbox checked={watch("accept_privacy_politics")} />
                </CheckboxContainer>
                <span>Aceito a </span>
                <LabelText
                  onClick={(e) => {
                    e.preventDefault();
                    showPrivacyPolicy();
                  }}
                >
                  política de privacidade
                </LabelText>
              </CheckboxLabel>
              <ErrorForm message={errors.accept_privacy_politics?.message} />
            </CheckboxRow>
          </CheckboxGroup>

          <ButtonContainer>
            <StyledButtonSalvar type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Loader size={18} /> : "Cadastrar"}
            </StyledButtonSalvar>
          </ButtonContainer>
        </FormContainer>
      </ContentWrapper>
    </MainContainer>
  );
};

const InternRegisterUser = (props) => {
  const { userType = null } = props || {};
  const location = useLocation();
  const currentUser = useSelector((state) => state.user.currentUser);
  const simplifiedFieldUser =
    location.state?.simplifiedFieldUser === true ||
    (isEngineeringUser(currentUser) && !canManageUsers(currentUser));

  if (simplifiedFieldUser) {
    return <FieldUserRegisterForm />;
  }

  return <FullInternRegisterUser userType={userType} />;
};

export default InternRegisterUser;
